import React, { useState, useMemo, useEffect } from 'react'
import { DataBlock } from '@fs/zion-ui'
import { HistoryNavigator, CoveragesHistory, MetadataHistory } from '..'
import { useIntlDateTime } from '../../../../hooks'
import transl from '../../ChangeHistory.transl'
import styles from '../../ChangeHistory.styles'
import getFormattedDate, { dateFormats } from '../../../../../../../shared/utils/dateFormatHelper'
import { useOverlayContext } from '../../../../../../../shared/providers/overlayContextProvider/OverlayContext'

export default function ChangeHistoryPanel({ changeHistory = [] }) {
  const [currentDateTime, setCurrentDateTime] = useState(null)
  const { closeOverlay } = useOverlayContext()

  useEffect(() => {
    if (!Array.isArray(changeHistory) || !changeHistory?.[0]?.date?.fromTimestamp) return
    setCurrentDateTime(
      getFormattedDate(
        changeHistory[0].date.fromTimestamp,
        changeHistory[0].date.toTimestamp,
        dateFormats.formattedFullWithTime
      )
    ) // use formattedFullWithTime so changes made at the same time appear on the same change
  }, [changeHistory])

  const [selectedChanges, setSelectedChanges] = useState(null)
  useEffect(() => {
    if (currentDateTime == null || !Array.isArray(changeHistory) || changeHistory.length < 1) return
    const indexOrChanges = getSelectedIndexOrChanges(changeHistory, currentDateTime)
    setSelectedChanges(() => (typeof indexOrChanges === 'number' ? changeHistory[indexOrChanges] : indexOrChanges))
  }, [changeHistory, currentDateTime])

  const formatDate = useIntlDateTime()

  const selectedTimestamp = selectedChanges?.date?.fromTimestamp
  const compactDate = getFormattedDate(selectedTimestamp, selectedChanges?.date?.toTimestamp, dateFormats.formatted)
  const HMSDate = useMemo(() => {
    if (!selectedTimestamp) return transl.unknown()
    const options = {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }
    return formatDate({ date: selectedTimestamp, options })
  }, [formatDate, selectedTimestamp])

  return (
    <>
      <HistoryNavigator history={changeHistory} onChange={setCurrentDateTime} currentDateTime={currentDateTime} />
      <DataBlock
        heading={compactDate || ''}
        data={
          <div css={styles.changeHistoryP}>
            {transl.changedAt(HMSDate, selectedChanges?.user?.contactName || transl.unknown())}
          </div>
        }
      />

      {changeHistory && selectedChanges && currentDateTime && (
        <>
          <CoveragesHistory onFinish={() => closeOverlay('groupHistoryOverlay')} selectedChanges={selectedChanges} />
          <MetadataHistory onFinish={() => closeOverlay('groupHistoryOverlay')} selectedChanges={selectedChanges} />
        </>
      )}
    </>
  )
}

function getSelectedIndexOrChanges(changeHistory, currentDateTime) {
  let changes
  if (changeHistory) {
    changeHistory.forEach((el, index) => {
      if (
        getFormattedDate(el?.date?.fromTimestamp, el?.date?.toTimestamp, dateFormats.formattedFullWithTime) ===
        currentDateTime
      ) {
        if (changes) changes = mergeMatchingChanges(changes, el)
        else changes = { ...el, index }
      }
    })
  }
  if (Object.keys(changes).length > 1) return changes
  return changes.index
}

export function mergeMatchingChanges(change1, change2) {
  const newChange = {
    coverages: null,
    metadata: { title: null, languages: null, archivalReferenceNumber: null, volumes: null, creator: null },
  }
  newChange.coverages = getDefinedValue(change1?.coverages, change2?.coverages)
  newChange.metadata.title = getDefinedValue(change1.metadata.title, change2.metadata.title)
  newChange.metadata.languages = getDefinedValue(change1.metadata.languages, change2.metadata.languages)
  newChange.metadata.volumes = getDefinedValue(change1.metadata.volumes, change2.metadata.volumes)
  newChange.metadata.archivalReferenceNumber = getDefinedValue(
    change1.metadata.archivalReferenceNumber,
    change2.metadata.archivalReferenceNumber
  )
  newChange.metadata.creator = getDefinedValue(change1.metadata.creator, change2.metadata.creator)
  return { ...change1, ...newChange }
}

function getDefinedValue(val1, val2) {
  if (val1 != null) return val1
  if (val2 != null) return val2
  return null
}
